import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import HoneycombLogo from './HoneycombLogo';
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isProfileOpen, setIsProfileOpen] = React.useState(false);
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const profileRef = React.useRef(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      setIsProfileOpen(false);
      navigate('/login');
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  const getInitials = (email) => {
    if (!email) return 'U';
    return email.charAt(0).toUpperCase();
  };

  return (
    <header className="header">
      <div className="container">
        <nav className="navbar">
          <Link to="/" className="logo">
            <HoneycombLogo size={40} />
            <span className="logo-text">SkillHive</span>
          </Link>

          <button
            className="menu-toggle"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          <ul className={`nav-links ${isMenuOpen ? 'active' : ''}`}>
            <li><Link to="/">Home</Link></li>
            <li><Link to="/services">Services</Link></li>
            <li><Link to="/about">About</Link></li>
            {currentUser && <li><Link to="/my-bookings">My Bookings</Link></li>}
          </ul>

          <div className="nav-actions">
            {currentUser ? (
              <div className="profile-dropdown" ref={profileRef}>
                <button
                  className="profile-trigger"
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  aria-label="Profile menu"
                >
                  <div className="profile-avatar">
                    {currentUser.photoURL ? (
                      <img src={currentUser.photoURL} alt="Profile" referrerPolicy="no-referrer" />
                    ) : (
                      <span>{getInitials(currentUser.email)}</span>
                    )}
                  </div>
                </button>

                {isProfileOpen && (
                  <div className="profile-dropdown-menu">
                    <div className="dropdown-header">
                      <div className="dropdown-user-info">
                        <strong>{currentUser.displayName || 'User'}</strong>
                        <span className="dropdown-email">{currentUser.email}</span>
                      </div>
                    </div>

                    <div className="dropdown-divider"></div>

                    <Link
                      to="/my-bookings"
                      className="dropdown-item"
                      onClick={() => setIsProfileOpen(false)}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M3 2h10a1 1 0 011 1v10a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1zm1 2v8h8V4H4z" />
                      </svg>
                      My Bookings
                    </Link>

                    <Link
                      to="/saved-addresses"
                      className="dropdown-item"
                      onClick={() => setIsProfileOpen(false)}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M8 1a3 3 0 00-3 3c0 2.5 3 6 3 6s3-3.5 3-6a3 3 0 00-3-3zm0 4.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" />
                      </svg>
                      Saved Addresses
                    </Link>

                    <Link
                      to="/profile"
                      className="dropdown-item"
                      onClick={() => setIsProfileOpen(false)}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M8 8a3 3 0 100-6 3 3 0 000 6zm0 1c-2.67 0-8 1.34-8 4v1h16v-1c0-2.66-5.33-4-8-4z" />
                      </svg>
                      Personal Details
                    </Link>

                    <Link
                      to="/settings"
                      className="dropdown-item"
                      onClick={() => setIsProfileOpen(false)}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M8 3.5a4.5 4.5 0 100 9 4.5 4.5 0 000-9zM6.5 8a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0z" />
                        <path d="M7 1v1.07a6 6 0 012 0V1h-2zm0 12.93V15h2v-1.07a6 6 0 01-2 0z" />
                      </svg>
                      Settings
                    </Link>

                    <div className="dropdown-divider"></div>

                    <button
                      onClick={handleLogout}
                      className="dropdown-item logout-item"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M6 2v2H2v8h4v2H2a2 2 0 01-2-2V4a2 2 0 012-2h4zm8.293 5.293L12 9.586V7a1 1 0 00-2 0v4a1 1 0 001 1h4a1 1 0 000-2h-2.586l2.293-2.293a1 1 0 00-1.414-1.414z" />
                      </svg>
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" className="btn btn-secondary">Login</Link>
                <Link to="/signup" className="btn btn-primary">Sign Up</Link>
              </>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
